import {
  isFresherPhoneCall,
  type PhoneCall,
  type PhoneCallDisclosureState,
  type PhoneCallDisposition,
  type PhoneCallHoldState,
  type PhoneCallOrigin,
  type PhoneCallOutcome,
  type PhoneCallStatus,
} from "@phone/phoneCallsClient";
import { create } from "zustand";

const CARD_STATUSES: ReadonlySet<string> = new Set<PhoneCallStatus>([
  "ringing",
  "disclosing",
  "handling",
  "dialing",
  "out_dialing_staff",
  "out_awaiting_confirmation",
  "out_dialing_recipient",
  "out_disclosing",
  "out_connecting",
  "bridged",
  "ended",
]);

/** An unknown status withdraws the card rather than label the call by a guess. */
export const isKnownCallStatus = (
  status: string | undefined,
): status is PhoneCallStatus =>
  status !== undefined && CARD_STATUSES.has(status);

export type LiveCallStatus = Exclude<PhoneCallStatus, "ended">;

/** `out_*` statuses are staff-outbound only (brian `models/phone_call.py`); `bridged` is shared. */
const originOf = (status: LiveCallStatus): PhoneCallOrigin | null =>
  status.startsWith("out_") ? "staff_outbound" : null;

/**
 * A `phone_call` frame fans out to every socket in the clinic (brian
 * `modules/notify/bus.py`; `PhoneCallOut` names no initiating user), so raising
 * on an `out_*` frame would show every screen a Cancel for someone else's call.
 * The placer's slip comes from `raise()`.
 */
const RAISES_ON_FRAME: ReadonlySet<LiveCallStatus> = new Set<LiveCallStatus>([
  "ringing",
  "disclosing",
  "handling",
  "dialing",
]);

/** Outbound only: an inbound slip's end takes the card away outright. */
type SettledCall =
  | {
      rowRead: true;
      disposition: PhoneCallDisposition | null;
      outcome: PhoneCallOutcome | null;
    }
  | { rowRead: false };

/** `label` is the frame's optimistic hint; the detail fields come from `GET /phone-calls`. */
export interface IncomingCall {
  callUuid: string;
  origin: PhoneCallOrigin;
  status: LiveCallStatus;
  label: string | null;
  callerName: string | null;
  /** The recipient on an outbound call. */
  clientName: string | null;
  clientUuid: string | null;
  fromNumber: string | null;
  clientPatients: string[] | null;
  recipientE164: string | null;
  /** Row only, no frame carries it; set live on `handoff_failed`, which ends Hold (`isHoldable`). */
  disposition: PhoneCallDisposition | null;
  startedAt: string | null;
  /** This clock's reading when the slip first saw `bridged`: the duration's floor while `startedAt` is unknown. */
  bridgedAtMs: number | null;
  settled: SettledCall | null;
  /** Last row version seen, from a frame or a GET, for `isFresherPhoneCall`. */
  revision: number | null;
  updatedAt: string | null;
  /** Repeated row reads failed; the card stays with a note. */
  unreachable: boolean;
  /** Separate from `status`: `bridged` means the leg joined, not that the notice finished. */
  disclosureState: PhoneCallDisclosureState;
  /** Separate from `status`, which stays where the hold found it. A held caller must never lose their slip. */
  holdState: PhoneCallHoldState;
  heldAt: string | null;
}

interface HoldFields {
  holdState: PhoneCallHoldState;
  heldAt: string | null;
}

const blank = (
  callUuid: string,
  origin: PhoneCallOrigin,
  status: LiveCallStatus,
  label: string | null,
  revision: number | null,
  updatedAt: string | null,
  disclosureState: PhoneCallDisclosureState,
  hold: HoldFields,
): IncomingCall => ({
  callUuid,
  origin,
  status,
  label,
  callerName: null,
  clientName: null,
  clientUuid: null,
  fromNumber: null,
  clientPatients: null,
  recipientE164: null,
  disposition: null,
  startedAt: null,
  bridgedAtMs: status === "bridged" ? Date.now() : null,
  settled: null,
  revision,
  updatedAt,
  unreachable: false,
  disclosureState,
  holdState: hold.holdState,
  heldAt: hold.heldAt,
});

const moved = (
  card: IncomingCall,
  status: LiveCallStatus,
  revision: number | null,
  updatedAt: string | null,
  disclosureState: PhoneCallDisclosureState,
  hold: HoldFields,
): IncomingCall => ({
  ...card,
  status,
  origin: originOf(status) ?? card.origin,
  bridgedAtMs:
    status === "bridged" ? (card.bridgedAtMs ?? Date.now()) : card.bridgedAtMs,
  // A versionless frame must not wipe a GET's version, or a stale in-flight GET could move the card backwards.
  revision: revision ?? card.revision,
  updatedAt: updatedAt ?? card.updatedAt,
  disclosureState,
  holdState: hold.holdState,
  heldAt: hold.heldAt,
});

const holdOf = (call: Pick<PhoneCall, "holdState" | "heldAt">): HoldFields => ({
  holdState: call.holdState,
  heldAt: call.heldAt,
});

const detailOf = (call: PhoneCall) => ({
  callerName: call.callerName,
  clientName: call.clientName,
  clientUuid: call.clientUuid,
  fromNumber: call.fromNumber,
  clientPatients: call.clientPatients,
  recipientE164: call.recipientE164,
  disposition: call.disposition,
  startedAt: call.startedAt,
});

interface IncomingCallState {
  calls: IncomingCall[];
  /** Dismissed or ended calls, so a still-running call's next frame can't re-raise a dismissed card. */
  closed: ReadonlySet<string>;
  show: (
    callUuid: string,
    label: string | null,
    status: LiveCallStatus,
    revision: number | null,
    updatedAt: string | null,
    disclosureState: PhoneCallDisclosureState,
    hold: HoldFields,
  ) => void;
  /** The `POST /outbound-calls` response, raised before its frame arrives. */
  raise: (call: PhoneCall) => void;
  /**
   * An "ended" row always wins (brian never un-ends a call) but lands only its
   * details; settling or closing is the caller's. A settled card takes no other
   * row: the ordering guard can't tell a pre-end row from a fresh one.
   */
  reconcileRow: (call: PhoneCall) => void;
  markUnreachable: (callUuid: string) => void;
  /** The "ended" frame settles with `rowRead: false`; the row upgrades it, never the reverse. */
  settle: (
    callUuid: string,
    settled: SettledCall,
    version?: Pick<PhoneCall, "revision" | "updatedAt">,
  ) => void;
  close: (callUuid: string) => void;
  withdraw: (callUuid: string) => void;
}

/**
 * An inbound card lives exactly as long as its call; an outbound slip settles
 * briefly so the placer sees how it went. Insertion order is call history, not
 * corner order (`IncomingCallCards`).
 */
export const useIncomingCallStore = create<IncomingCallState>((set) => ({
  calls: [],
  closed: new Set(),
  show: (callUuid, label, status, revision, updatedAt, disclosureState, hold) =>
    set((s) => {
      // A dismissed card returns once its caller is held; the held slip has no
      // Dismiss, so this can't loop against a fresh dismissal.
      const rehold =
        s.closed.has(callUuid) &&
        hold.holdState !== "none" &&
        !s.calls.some((c) => c.callUuid === callUuid);
      if (s.closed.has(callUuid) && !rehold) return s;
      const closed = rehold
        ? (() => {
            const next = new Set(s.closed);
            next.delete(callUuid);
            return next;
          })()
        : s.closed;
      const existing = s.calls.find((c) => c.callUuid === callUuid);
      if (existing) {
        if (existing.settled) return s;
        if (
          !isFresherPhoneCall(
            {
              status: existing.status,
              revision: existing.revision,
              updatedAt: existing.updatedAt,
            },
            { status, revision, updatedAt },
          )
        )
          return s;
        return {
          calls: s.calls.map((c) =>
            c.callUuid === callUuid
              ? {
                  ...moved(
                    c,
                    status,
                    revision,
                    updatedAt,
                    disclosureState,
                    hold,
                  ),
                  label,
                }
              : c,
          ),
        };
      }
      if (!RAISES_ON_FRAME.has(status)) return s;
      return {
        closed,
        calls: [
          ...s.calls,
          blank(
            callUuid,
            "inbound",
            status,
            label,
            revision,
            updatedAt,
            disclosureState,
            hold,
          ),
        ],
      };
    }),
  raise: (call) =>
    set((s) => {
      if (
        s.closed.has(call.id) ||
        call.status === "ended" ||
        s.calls.some((c) => c.callUuid === call.id)
      )
        return s;
      return {
        calls: [
          ...s.calls,
          {
            ...blank(
              call.id,
              call.origin,
              call.status,
              null,
              call.revision,
              call.updatedAt,
              call.disclosureState,
              holdOf(call),
            ),
            ...detailOf(call),
          },
        ],
      };
    }),
  reconcileRow: (call) =>
    set((s) => {
      const card = s.calls.find((c) => c.callUuid === call.id);
      if (!card) return s;
      if (call.status === "ended") {
        return {
          calls: s.calls.map((c) =>
            c.callUuid === call.id
              ? {
                  ...c,
                  ...detailOf(call),
                  revision: call.revision ?? c.revision,
                  updatedAt: call.updatedAt ?? c.updatedAt,
                  unreachable: false,
                }
              : c,
          ),
        };
      }
      if (card.settled) return s;
      const status: LiveCallStatus = call.status;
      if (
        !isFresherPhoneCall(
          {
            status: card.status,
            revision: card.revision,
            updatedAt: card.updatedAt,
          },
          { status, revision: call.revision, updatedAt: call.updatedAt },
          { allowEqual: true },
        )
      )
        return s;
      return {
        calls: s.calls.map((c) =>
          c.callUuid === call.id
            ? {
                ...moved(
                  c,
                  status,
                  call.revision,
                  call.updatedAt,
                  call.disclosureState,
                  holdOf(call),
                ),
                ...detailOf(call),
                unreachable: false,
              }
            : c,
        ),
      };
    }),
  markUnreachable: (callUuid) =>
    set((s) => ({
      calls: s.calls.map((c) =>
        c.callUuid === callUuid && !c.settled && !c.unreachable
          ? { ...c, unreachable: true }
          : c,
      ),
    })),
  settle: (callUuid, settled, version) =>
    set((s) => {
      const card = s.calls.find((c) => c.callUuid === callUuid);
      if (!card || (card.settled?.rowRead && !settled.rowRead)) return s;
      if (
        version &&
        !isFresherPhoneCall(
          {
            status: card.settled ? "ended" : card.status,
            revision: card.revision,
            updatedAt: card.updatedAt,
          },
          { status: "ended", ...version },
          { allowEqual: true },
        )
      )
        return s;
      return {
        calls: s.calls.map((c) =>
          c.callUuid === callUuid
            ? {
                ...c,
                revision: version?.revision ?? c.revision,
                updatedAt: version?.updatedAt ?? c.updatedAt,
                settled,
                unreachable: false,
              }
            : c,
        ),
      };
    }),
  close: (callUuid) =>
    set((s) => ({
      calls: s.calls.filter((c) => c.callUuid !== callUuid),
      closed: new Set(s.closed).add(callUuid),
    })),
  withdraw: (callUuid) =>
    set((s) => {
      const calls = s.calls.filter((c) => c.callUuid !== callUuid);
      if (!s.closed.has(callUuid)) {
        return calls.length === s.calls.length ? s : { calls };
      }
      const closed = new Set(s.closed);
      closed.delete(callUuid);
      return { calls, closed };
    }),
}));
